import React, { useState } from "react";
import { Button, Grid, Typography, styled } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch } from "react-redux";
import { IEventLedgerList } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { formatDateString } from "src/components/DateDisplay";
import { debounce } from "lodash";

export interface IManualAcknowledgementProps {
  pairingLedger: IEventLedgerList;
  style?: any;
}

const OCManualAcknowledgementCell = styled("div")(({ theme }) => ({
  display: "flex",
  padding: 10,
  flexFlow: "row",
  "& .acknowledge_ledger_btn": {
    padding: 5,
    minWidth: 0,
  },
  "& .small_text": {
    marginLeft: 10,
    fontSize: 12,
    color: theme.custom.forms.inputText,
  },
  "& .acknowledged": {
    width: 36,
    height: 36,
    backgroundColor: theme.palette.success.light,
    color: theme.palette.background.default,
    padding: 5,
    borderRadius: 5,
  },
}));

/**
 * A cell in the table that contains a button to manually acknowledge a pairing/nonflyingassingment change for a given crew member.
 * @param props - crew id, pairing id
 */
export const ManualAcknowledgement = (props: IManualAcknowledgementProps): JSX.Element => {
  const { pairingLedger } = props;
  const dispatch = useDispatch();

  const [postingAcknowledgement, setPostingAcknowledgement] = useState<boolean>(false);
  const crewId = pairingLedger?.crewMember?._id;
  const isAcknowledged = pairingLedger?.acknowledged?.acknowledgedBy;

  const handleClick = async (event: React.MouseEvent): Promise<void> => {
    event.stopPropagation(); // important when used in the table where rows are clickable.
    if (!crewId) {
      return;
    }
    setPostingAcknowledgement(true);
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Acknowledging Change...",
      successMessage: "Successfully Acknowledged.",
      errorMessage: "Error: Unable to acknowledge.",
    };
    const response: IEventLedgerList[] = await AmplifyAPIWrapper.Post<IEventLedgerList[]>(
      `/eventledgers/crews/${crewId}`,
      [pairingLedger],
      notifications
    );
    if (Array.isArray(response) && !!response[0]?.acknowledged?.acknowledgedBy) {
      //! Mutating the original object shouldn't be a problem here but if so we can switch it to include a useState in this component.
      pairingLedger.acknowledged = response[0].acknowledged;
    }
    setPostingAcknowledgement(false);
  };

  return (
    <OCManualAcknowledgementCell>
      {!isAcknowledged ? (
        <Grid container wrap="nowrap" alignItems="center">
          <Button
            className={"acknowledge_ledger_btn"}
            variant="outlined"
            onClick={debounce(handleClick, 1000)}
            disabled={!crewId}
          >
            {!postingAcknowledgement ? (
              <CheckIcon style={{ width: 24, height: 24 }} />
            ) : (
              <CircularProgress style={{ width: 24, height: 24 }} />
            )}
          </Button>
          <Typography className="small_text">
            {!postingAcknowledgement ? "Acknowledge Change" : "Acknowledging Change..."}
          </Typography>
        </Grid>
      ) : (
        <Grid container wrap="nowrap" alignItems="center">
          <CheckIcon className="acknowledged" />
          <Grid container direction="column">
            <Typography className="small_text">
              {`Acknowledged on ${formatDateString(pairingLedger?.acknowledged?.acknowledgedAt, "Etc/UTC", true)}`}
            </Typography>
            <Typography className="small_text">
              {`By ${pairingLedger?.acknowledged?.firstName} ${pairingLedger?.acknowledged?.lastName}`}
            </Typography>
          </Grid>
        </Grid>
      )}
    </OCManualAcknowledgementCell>
  );
};
